import classNames from 'classnames';
import { get, isEmpty } from 'lodash';
import { FC, useMemo } from 'react';
import { useRecoilValue } from 'recoil';
import { CountriesAtom, Country } from '~/services/countries';
import Input from '../Input';
import Select from '../Select';

interface AddressFormProps {
    formik: any;
    type?: 'billing_address' | 'shipping_address';
    className?: string;
    loading?: boolean;
}

const AddressForm: FC<AddressFormProps> = ({ formik, type = 'shipping_address', className, loading }) => {
    const { countries } = useRecoilValue(CountriesAtom.countriesSelector);

    const valuePrefix = type ? type + '.' : '';
    const country_code = get(formik, `values.${valuePrefix}country_code`);
    const region_id = get(formik, `values.${valuePrefix}region_id`);

    const avalidRegions: any[] = useMemo(() => {
        const result = countries?.find((c: Country) => c.value === country_code)?.available_regions || [];

        return result?.map((r: any) => ({ label: r.name, value: r.id, code: r.code })) || [];
    }, [countries, country_code]);

    return (
        <div className={classNames('flex flex-col gap-4', className)}>
            <div className="flex gap-3">
                <Input
                    key={`${type}.firstname`}
                    label="First name"
                    className="w-full"
                    required
                    placeholder="Enter first name"
                    {...formik.getFieldProps(`${valuePrefix}firstname`)}
                    error={
                        // get(formik, `touched.${valuePrefix}firstname`) &&
                        get(formik, `errors.${valuePrefix}firstname`)
                    }
                    id={`${valuePrefix}firstname`}
                    disabled={loading}
                />
                <Input
                    label="Last name"
                    className="w-full"
                    required
                    placeholder="Enter last name"
                    {...formik.getFieldProps(`${valuePrefix}lastname`)}
                    error={
                        // get(formik, `touched.${valuePrefix}lastname`) &&
                        get(formik, `errors.${valuePrefix}lastname`)
                    }
                    id={`${valuePrefix}lastname`}
                    disabled={loading}
                />
            </div>

            <Input
                label="Address"
                required
                placeholder="Enter street address"
                {...formik.getFieldProps(`${valuePrefix}street`)}
                error={
                    // get(formik, `touched.${valuePrefix}street`) &&
                    get(formik, `errors.${valuePrefix}street`)
                }
                id={`${valuePrefix}street`}
                disabled={loading}
            />
            <Input
                placeholder="Enter Apartment, suite, etc. (optional)"
                {...formik.getFieldProps(`${valuePrefix}street1`)}
                error={
                    // get(formik, `touched.${valuePrefix}street`) &&
                    get(formik, `errors.${valuePrefix}street1`)
                }
                id={`${valuePrefix}street1`}
                disabled={loading}
            />

            <div className="flex gap-3">
                <Select
                    required
                    options={countries || []}
                    className="w-full"
                    label="Country"
                    value={country_code}
                    onBlur={(e: any) => {
                        formik.getFieldProps(`${valuePrefix}country_code`).onBlur(e);
                    }}
                    onChange={(e) => {
                        formik.setFieldValue(`${valuePrefix}country_code`, e);
                        formik.setFieldValue(`${valuePrefix}region_id`, null);
                        formik.setFieldValue(`${valuePrefix}region_code`, null);
                    }}
                    error={
                        // get(formik, `touched.${valuePrefix}country_code`) &&
                        get(formik, `errors.${valuePrefix}country_code`)
                    }
                    id={`${valuePrefix}country_code`}
                    isDisabled={loading}
                />

                {!isEmpty(avalidRegions) && (
                    <Select
                        required
                        options={avalidRegions}
                        label="Region"
                        value={region_id}
                        className="w-full"
                        onBlur={(e: any) => {
                            formik.getFieldProps(`${valuePrefix}region_id`).onBlur(e);
                        }}
                        onChange={(e, option) => {
                            formik.setFieldValue(`${valuePrefix}region_id`, e);
                            formik.setFieldValue(`${valuePrefix}region_code`, option.code);
                        }}
                        error={
                            // get(formik, `touched.${valuePrefix}region_code`) &&
                            get(formik, `errors.${valuePrefix}region_id`)
                        }
                        id={`${valuePrefix}region_id`}
                        name={`${valuePrefix}region_id`}
                        isDisabled={loading}
                    />
                )}
            </div>
            <div className="flex gap-3">
                <Input
                    label="Town/City"
                    className="w-full"
                    required
                    disabled={loading}
                    placeholder="Enter town/city"
                    {...formik.getFieldProps(`${valuePrefix}city`)}
                    error={
                        // get(formik, `touched.${valuePrefix}city`) &&
                        get(formik, `errors.${valuePrefix}city`)
                    }
                    id={`${valuePrefix}city`}
                />
                <Input
                    label="ZIP"
                    required
                    className="w-full"
                    placeholder="Enter zip"
                    disabled={loading}
                    {...formik.getFieldProps(`${valuePrefix}postcode`)}
                    error={
                        // get(formik, `touched.${valuePrefix}postcode`) &&
                        get(formik, `errors.${valuePrefix}postcode`)
                    }
                    id={`${valuePrefix}postcode`}
                />
            </div>
            <Input
                label="Phone number"
                className="w-full"
                // required
                disabled={loading}
                placeholder="Enter phone number"
                {...formik.getFieldProps(`${valuePrefix}telephone`)}
                error={
                    // get(formik, `touched.${valuePrefix}telephone`) &&
                    get(formik, `errors.${valuePrefix}telephone`)
                }
                id={`${valuePrefix}telephone`}
            />
        </div>
    );
};

export default AddressForm;
